import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, ClipboardList, MessageSquare, User, BarChart, ChevronLeft, ChevronRight, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';

interface NavItem {
  to: string;
  icon: React.ElementType;
  label: string;
  exactMatch?: boolean;
}

export function DesktopSidebar() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const { logout } = useAuth();
  const { data: user } = useCurrentUser();

  const navItems: NavItem[] = [
    { to: '/welcome', icon: Home, label: 'Welcome', exactMatch: true },
    { to: '/profile', icon: User, label: 'Profile' },
    { to: '/survey', icon: ClipboardList, label: 'Assessments' },
    { to: '/coaching', icon: MessageSquare, label: 'Coaching' },
    { to: '/updates', icon: BarChart, label: 'Progress' },
  ];

  const handleLogout = async () => {
    await logout();
  };

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 h-full bg-white border-r border-gray-200 transition-all duration-300 z-40",
        isCollapsed ? "w-20" : "w-64"
      )}
    >
      <div className="flex flex-col h-full">
        {/* Header with user info */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className={cn(
              "flex items-center gap-3 transition-opacity",
              isCollapsed && "opacity-0 pointer-events-none"
            )}>
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-semibold">
                  {user?.firstName?.[0]?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="overflow-hidden">
                <p className="font-medium text-sm truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="ml-auto"
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => {
            const isActive = item.exactMatch 
              ? location.pathname === item.to 
              : location.pathname.startsWith(item.to);
            
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                  "hover:bg-gray-100 focus:bg-gray-100 focus:outline-none",
                  isActive && "bg-primary/10 text-primary hover:bg-primary/15",
                  !isActive && "text-gray-700"
                )}
              >
                <item.icon className={cn(
                  "flex-shrink-0 transition-all",
                  isCollapsed ? "h-5 w-5" : "h-5 w-5"
                )} />
                <span className={cn(
                  "transition-opacity",
                  isCollapsed && "opacity-0 pointer-events-none"
                )}>
                  {item.label}
                </span>
                {isCollapsed && (
                  <div className="absolute left-full ml-6 px-2 py-1 bg-gray-900 text-white text-sm rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity">
                    {item.label}
                  </div>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Logout button */}
        <div className="p-3 border-t border-gray-200">
          <Button
            variant="ghost"
            className={cn(
              "w-full justify-start gap-3 text-gray-700 hover:text-gray-900",
              isCollapsed && "justify-center"
            )}
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            <span className={cn(
              "transition-opacity",
              isCollapsed && "opacity-0 pointer-events-none"
            )}>
              Log out
            </span>
          </Button>
        </div>
      </div>
    </aside>
  );
}